import { useState, useCallback, useEffect, useMemo, useContext } from "react";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import { config } from "../../config.js";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import { Link, useNavigate } from "react-router-dom";
import "./City.css";
import { ToastContainer, toast } from "react-toastify";
import { FaEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import CustomSelect from "../../components/CustomSelect/CustomSelect.jsx";
import { LuArrowLeft } from "react-icons/lu";

/* ================= CITY LIST ================= */

const CityList = () => {
  const navigate = useNavigate();
  const { isSidebar } = useContext(SidebarContext);
  const token = localStorage.getItem("adminToken");

  const [loading, setLoading] = useState(true);
  const [cityList, setCityList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ---------- FETCH CITIES ---------- */
  const getCities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}city/get?limit=100`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) throw new Error();

      const result = await response.json();
      setCityList(Array.isArray(result.cities) ? result.cities : []);
    } catch (err) {
      console.error(err);
      setCityList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCities();
  }, [getCities]);

  const fetchStates = useCallback(async () => {
    try {
      const response = await fetch(`${config.backEndBaseUrl}city/states`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error();

      const result = await response.json();
      setStateList(result.states || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  /* ---------- STATUS UPDATE ---------- */
  const handleUpdate = (uid, newStatus, key) => {
    setCityList((prev) =>
      prev.map((item) =>
        item.uid === uid ? { ...item, [key]: newStatus } : item,
      ),
    );
  };

  /* ---------- TOGGLE ---------- */
  const ToggleButtonStatus = ({ uid, initialStatus, onUpdate }) => {
    const [status, setStatus] = useState(initialStatus);

    const toggleStatus = async () => {
      const next = !status;
      setStatus(next);

      try {
        const res = await fetch(
          `${config.backEndBaseUrl}city/updateStatus/${uid}?active=${next}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!res.ok) throw new Error();
        onUpdate(uid, next);
      } catch {
        setStatus(!next);
      }
    };

    return (
      <div onClick={toggleStatus} className="cursor-pointer">
        <div className={`toggle-switch ${status ? "active" : ""}`}>
          <div className="toggle-knob" />
        </div>
      </div>
    );
  };

  /* ---------- CONFIRM DELETE ---------- */
  const confirmDelete = async () => {
    if (!confirmDeleteId) return;

    setDeletingId(confirmDeleteId);

    try {
      const response = await fetch(
        `${config.backEndBaseUrl}city/delete/${confirmDeleteId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) throw new Error();

      setCityList((prev) =>
        prev.filter((city) => city.uid !== confirmDeleteId),
      );

      toast.success("City deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete city");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  /* ---------- TABLE ---------- */
  const columns = useMemo(
    () => [
      // { accessorKey: "country", header: "Country" },
      { accessorKey: "state", header: "State" },
      {
        accessorKey: "city",
        header: "City",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value
            ? String(value).charAt(0).toUpperCase() + String(value).slice(1)
            : "-";
        },
      },
      {
        accessorKey: "uid",
        header: "Fares",
        muiTableHeadCellProps: {
          sx: {
            width: "120px !important",
            minWidth: "120px !important",
            maxWidth: "120px !important",
            textAlign: "center",
          },
        },

        muiTableBodyCellProps: {
          sx: {
            width: "120px !important",
            minWidth: "120px !important",
            maxWidth: "120px !important",
            textAlign: "center",
          },
        },
        Cell: ({ cell }) => {
          const city = cell.row.original.city;
          return (
            <div className="flex gap-2">
              <Link
                to={`/fare/${city.toLocaleLowerCase()}`}
                className="btn btn-sm btn-outline-success d-flex align-items-center"
              >
                Manage
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: "uid",
        header: "Night Charges",
        Cell: ({ cell }) => {
          const city = cell.row.original.city;
          return (
            <div className="flex gap-2">
              <Link
                to={`/night-charges/${city.toLocaleLowerCase()}`}
                className="btn btn-sm btn-outline-success d-flex align-items-center"
              >
                Manage
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: "active",
        header: "Status",
        size: 100,
        maxSize: 100,
        Cell: ({ cell }) => (
          <ToggleButtonStatus
            uid={cell.row.original.uid}
            initialStatus={cell.getValue()}
            onUpdate={(uid, val) => handleUpdate(uid, val, "active")}
          />
        ),
      },
      {
        accessorKey: "uid",
        header: "Actions",
        Cell: ({ cell }) => {
          const uid = cell.row.original.uid;
          return (
            <div className="flex gap-2">
              <Link
                to={`/city/${uid}`}
                className="btn btn-sm btn-success d-flex align-items-center"
              >
                <FaEye />
              </Link>
              <button
                className="btn btn-sm btn-danger"
                disabled={deletingId === uid}
                onClick={() => setConfirmDeleteId(uid)}
              >
                {deletingId === uid ? "..." : <RiDeleteBin6Line />}
              </button>
            </div>
          );
        },
      },
    ],
    [deletingId],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <ToastContainer />

                  <div className="wg-box">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <button
                            className="back-btn flex items-center gap-2"
                            onClick={() => navigate("/")}
                          >
                            <LuArrowLeft size={16} />
                            {/* <span>Back</span> */}
                          </button>

                          <h5 className="fare-heading">Cities</h5>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="btn btn-success"
                      >
                        Add City
                      </button>
                    </div>

                    {loading ? (
                      <div className="skeleton-table">
                        {[...Array(6)].map((_, i) => (
                          <div className="skeleton-row" key={i}>
                            <div className="skeleton skeleton-name"></div>
                            <div className="skeleton skeleton-status"></div>
                            <div className="skeleton skeleton-phone"></div>
                            <div className="skeleton skeleton-email"></div>
                            <div className="skeleton skeleton-status"></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ReactTable columns={columns} data={cityList} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddCityModal
          stateList={stateList}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            getCities();
            toast.success("City added");
          }}
        />
      )}

      {confirmDeleteId && (
        <ConfirmModal
          loading={deletingId === confirmDeleteId}
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

/* ================= CONFIRM DELETE MODAL ================= */

const ConfirmModal = ({ onCancel, onConfirm, loading }) => (
  <div className="modal-backdrop">
    <div className="modal-card" style={{ maxWidth: 420 }}>
      <div className="modal-header">
        <h3 className="modal-title">Delete City</h3>
      </div>

      <div className="modal-body">
        <p>
          Are you sure you want to delete this city?
          <br />
          <strong>This action cannot be undone.</strong>
        </p>
      </div>

      <div className="modal-footer">
        <button onClick={onCancel} className="btn btn-dark">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="btn btn-danger"
        >
          {loading ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

/* ================= ADD CITY MODAL ================= */

const AddCityModal = ({ onClose, onSuccess, stateList }) => {
  const [city, setCity] = useState("");
  const [carTypes, setCarTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState("");

  const CAR_TYPES = ["luxury", "hatchback", "sedan", "suv"];

  const toggleCarType = (type) => {
    setCarTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const submitCity = async () => {
    if (!city.trim() || !state) {
      toast.warn("City name and state are required");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${config.backEndBaseUrl}city/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          city,
          state,
          country: "India",
          carTypes,
        }),
      });

      if (!response.ok) throw new Error();
      onSuccess();
    } catch {
      toast.error("Failed to create city");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <h3 className="modal-title">Add City</h3>
          <button onClick={onClose} className="modal-close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Country</label>
            <input
              value="India"
              disabled
              className="form-input form-input-disabled"
            />
          </div>

          <div className="form-group">
            <label className="form-label">State</label>
            <CustomSelect
              options={stateList?.map((s) => s.stateName) || []}
              value={state}
              placeholder="Select State"
              onChange={(val) => setState(val)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">City Name</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="form-input"
              placeholder="Enter city name"
            />
          </div>

          {/* <div className="form-group">
                        <label className="form-label">Car Types</label>
                        <div className="chip-group">
                            {CAR_TYPES.map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => toggleCarType(type)}
                                    className={`chip ${carTypes.includes(type) ? 'chip-active' : ''}`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div> */}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-dark">
            Cancel
          </button>
          <button
            onClick={submitCity}
            disabled={saving}
            className="btn btn-success"
          >
            {saving ? "Saving..." : "Save City"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CityList;
